import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FullPageMessage } from "./FullPageMessage";
import { completeLogin } from "./session";
import { useAuth } from "./useAuth";

type Failure = { kind: "disabled" } | { kind: "error"; message: string };

/** /auth/callback: the login page sends the browser here with ?code=… */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { completeSignIn, login } = useAuth();
  const [failure, setFailure] = useState<Failure | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // The code works once: guard against React running the effect twice.
    if (started.current) return;
    started.current = true;

    void (async () => {
      const result = await completeLogin(new URLSearchParams(window.location.search));
      if (result.kind === "redirecting") return;
      if (result.kind === "disabled" || result.kind === "error") {
        setFailure(result);
        return;
      }
      await completeSignIn();
      navigate(result.returnTo, { replace: true });
    })();
  }, [completeSignIn, navigate]);

  if (failure?.kind === "disabled") {
    return (
      <FullPageMessage title="Your account is disabled">
        <p>
          An MDF administrator has disabled this account, so you can't sign in right now. If you
          think this is a mistake, please contact your MDF administrator.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate("/", { replace: true })}>
            Back to home
          </Button>
          <Button variant="ghost" onClick={() => void login()}>
            Use another account
          </Button>
        </div>
      </FullPageMessage>
    );
  }
  if (failure) {
    return (
      <FullPageMessage title={failure.message}>
        <Button className="mt-2" onClick={() => void login()}>
          Sign in again
        </Button>
      </FullPageMessage>
    );
  }
  return <FullPageMessage spinner title="Signing you in…" />;
}
